import React from "react";
import { Box, Flex } from "theme-ui";
import { themevals } from "theme/themevals";

/**
 * Shared formatting and shell pieces for the redesigned dashboard.
 *
 * Every number on this screen comes from GET /dashboard/admin/overview.
 * Nothing here recomputes a figure - the metric layer is the one place
 * definitions live (DASHBOARD_REDESIGN_SPEC section 7.12).
 */

export const currency = (value: number): string =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);

export const currencyExact = (value: number): string =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);

export const compactCurrency = (value: number): string => {
  if (Math.abs(value) >= 1000000) return "$" + (value / 1000000).toFixed(1) + "M";
  if (Math.abs(value) >= 1000) return "$" + Math.round(value / 1000) + "k";
  return "$" + Math.round(value);
};

/**
 * Percent change with its own empty case. A null change means there was
 * no previous period to compare against - which is not the same as no
 * change, and must not render as "0%".
 */
export const changeText = (
  changePercent: number | null,
  suffix = "vs previous period",
): string => {
  if (changePercent === null) return "No comparable period";
  const arrow = changePercent >= 0 ? "Up" : "Down";
  return arrow + " " + Math.abs(changePercent).toFixed(1) + "% " + suffix;
};

export const Panel: React.FC<{
  title?: string;
  subtitle?: string;
  right?: React.ReactNode;
  children: React.ReactNode;
  sx?: Record<string, any>;
}> = ({ title, subtitle, right, children, sx }) => (
  <Box
    sx={{
      backgroundColor: "#FFFFFF",
      borderRadius: "6px",
      border: "1px solid",
      borderColor: themevals.colors.inputOutline,
      boxShadow: "0px 0px 12px 4px rgba(129, 129, 129, 0.07)",
      ...(sx || {}),
    }}
  >
    {(title || right) && (
      <Flex
        sx={{
          alignItems: "center",
          justifyContent: "space-between",
          gap: "12px",
          padding: "16px",
          borderBottom: "1px solid",
          borderColor: themevals.colors.inputOutline,
        }}
      >
        <Box>
          <Box
            sx={{
              fontFamily: "Poppins, sans-serif",
              fontSize: "14px",
              fontWeight: 600,
              color: "black",
            }}
          >
            {title}
          </Box>
          {subtitle && (
            <Box
              sx={{
                fontFamily: "Poppins, sans-serif",
                fontSize: "12px",
                fontWeight: 400,
                color: themevals.colors.dkgrey,
                marginTop: "2px",
              }}
            >
              {subtitle}
            </Box>
          )}
        </Box>
        {right}
      </Flex>
    )}
    <Box sx={{ padding: "16px" }}>{children}</Box>
  </Box>
);

/**
 * The three emptinesses, one component (spec section 6). A legitimate
 * zero shows the zero with its denominator; a gap someone should close
 * says what closes it; a gap that fills on its own says what fills it.
 * Never a blank rectangle, never a bare "0".
 */
export const EmptyState: React.FC<{ headline: string; detail?: string }> = ({
  headline,
  detail,
}) => (
  <Box sx={{ padding: "20px 4px", fontFamily: "Poppins, sans-serif" }}>
    <Box sx={{ fontSize: "14px", fontWeight: 600, color: "base_800" }}>
      {headline}
    </Box>
    {detail && (
      <Box
        sx={{
          fontSize: "12px",
          color: themevals.colors.dkgrey,
          marginTop: "4px",
          maxWidth: "460px",
          lineHeight: 1.5,
        }}
      >
        {detail}
      </Box>
    )}
  </Box>
);

export const Skeleton: React.FC<{ height?: string }> = ({ height = "120px" }) => (
  <Box
    sx={{
      backgroundColor: "#F5F5F5",
      borderRadius: "6px",
      height,
      width: "100%",
    }}
  />
);
